import { Document, Image, Page, StyleSheet, Text, View, Link } from '@react-pdf/renderer';
import React, { memo } from 'react';
import { IGetRegistration } from '../../services/hooks/Registration/useGetRegistration';
import { IRegistrationDocument } from '../../services/hooks/Registration/useVacancyDocuments';
import { configApi } from '../../services/configApi';
import { IExemptions } from '../../services/hooks/CandidateArea/useRequestMyExemption';

interface IOwnProps {
  data?: IGetRegistration | null;
  portraitLogo?: string;
  instituteLogo?: string;
  documents?: IRegistrationDocument[];
  exemptions?: IExemptions[];
}

const getAttachmentUrl = (attachment: { id: string; name: string }) => {
  const extension = attachment.name.split('.').pop()?.toLowerCase();
  if (!extension || !configApi.defaults.baseURL) return undefined;

  return new URL(`media/attachment/registration/${attachment.id}.${extension}`, configApi.defaults.baseURL).toString();
};

const ProofRegistration: React.FC<IOwnProps> = ({
  data,
  portraitLogo,
  instituteLogo,
  documents,
  exemptions,
}: IOwnProps) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: '#ffffff',
      padding: '30px 20px',
      fontSize: 9,
      marginHorizontal: '50px',
      height: '100%',
      alignItems: 'center',
    },
    logo: {
      width: 60,
      height: 100,
    },
    title: {
      marginTop: 25,
    },
    boxInfo: {
      flexDirection: 'column',
      alignItems: 'flex-end',
      width: '100%',
      marginTop: 25,
      marginBottom: 25,
    },
    subTitle: {
      textAlign: 'center',
      marginBottom: 10,
    },
    candidateInformation: {
      width: '100%',
      padding: 5,
      marginTop: 25,
    },
    candidateValue: {
      paddingTop: 7,
      paddingBottom: 7,
      paddingLeft: 5,
    },
    signatureBox: {
      borderTop: '1px solid black',
      width: '38%',
      paddingTop: 7,
      alignItems: 'center',
    },
    footer: {
      width: '80%',
      flexDirection: 'row',
      marginTop: 30,
    },
    linkText: {
      fontSize: 8,
      color: 'blue',
      textDecoration: 'underline',
      paddingLeft: 15,
    },
    linkGroup: { marginTop: 3, fontSize: 8, color: 'blue', textDecoration: 'underline', paddingLeft: 15 },
    specialCondition: {
      marginTop: 15,
    },
    textSpecialCondition: {
      paddingBottom: 7,
      paddingLeft: 15,
      marginTop: 10,
    },
    linkTextSpecialCondition: {
      fontSize: 8,
      color: 'blue',
      textDecoration: 'underline',
      paddingLeft: 30,
    },
  });

  const address = data?.candidate;
  const listAttachPCD =
    data?.reserved_vacancy.filter((list) => list.type.toLowerCase().includes('pcd'))[0] ?? undefined;
  const uploadedDocuments = documents?.filter((document) => document.uploaded && document.attachment) ?? [];
  return (
    <Document>
      <Page size="A4" style={{ justifyContent: 'space-around' }}>
        <View style={styles.page}>
          <View
            style={{
              display: 'flex',
              height: '100px',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Image source={portraitLogo} style={styles.logo} />
            <Image source={instituteLogo} style={styles.logo} />
          </View>

          {data?.exemptions && data?.exemptions?.length > 0 && (
            <Text style={styles.title}>{'COMPROVANTE DE ISENÇÃO'}</Text>
          )}

          {data?.exemptions && data?.exemptions?.length === 0 && (
            <Text style={styles.title}>{'COMPROVANTE DE INSCRIÇÃO'}</Text>
          )}

          <View style={styles.boxInfo}>
            <Text>Número da inscrição: {data?.registration_code}</Text>
            <Text>Data e hora da inscrição: {data?.created_at?.br || ''}</Text>
          </View>
          <Text style={styles.subTitle}>
            PROCESSO SELETIVO: {data?.selective_process_vacancy?.selective_process.name || '--'}
          </Text>

          {data?.selective_process_vacancy.cultural_house && (
            <>
              <Text style={styles.subTitle}>
                {data?.selective_process_vacancy.class_level ? 'Turma: ' : 'Turma - Primeira Opção:'}
                {data?.selective_process_vacancy?.name || '--'}
              </Text>
              {data?.second_vacancy?.name && (
                <Text style={styles.subTitle}>Turma - segunda Opção: {data?.second_vacancy?.name || '--'}</Text>
              )}
            </>
          )}

          {!data?.selective_process_vacancy.cultural_house && (
            <Text style={styles.subTitle}>Vaga/Cargo: {data?.selective_process_vacancy?.name || '--'}</Text>
          )}

          <View style={styles.candidateInformation}>
            <Text style={styles.candidateValue}>
              NOME COMPLETO: <Text>{data?.candidate?.user.full_name || '--'}</Text>
            </Text>

            {data?.candidate?.social_name && (
              <Text style={styles.candidateValue}>
                NOME SOCIAL: <Text>{data?.candidate?.social_name || '--'}</Text>
              </Text>
            )}

            {data?.candidate.is_foreign ? (
              <Text style={styles.candidateValue}>
                PASSAPORTE: <Text>{data?.candidate?.passport_number || '--'}</Text>
              </Text>
            ) : (
              <Text style={styles.candidateValue}>
                CPF: <Text>{data?.candidate?.cpf || '--'}</Text>
              </Text>
            )}

            {data?.candidate.is_foreign ? (
              <></>
            ) : (
              <Text style={styles.candidateValue}>
                DOCUMENTO DE IDENTIFICAÇÃO:{' '}
                <Text>{`${data?.candidate?.document_type?.name || ''}: ${data?.candidate?.document_number || ''} - ${data?.candidate?.document_issuing_body || ''} - ${data?.candidate?.document_issuing_body_uf?.initials || ''} - expedido: ${data?.candidate?.document_date_of_issuance?.br || ''}`}</Text>
              </Text>
            )}

            <Text style={styles.candidateValue}>
              SEXO: <Text>{data?.candidate?.gender?.name || '--'}</Text>
            </Text>
            <Text style={styles.candidateValue}>
              DATA DE NASCIMENTO: <Text>{data?.candidate?.date_of_birth.br || '--'}</Text>
            </Text>
            {address?.address_street && (
              <Text style={styles.candidateValue}>
                ENDEREÇO:{' '}
                <Text>{`${address?.address_street || '--'}, N° ${address?.address_number || '--'}, ${address?.address_complement + ',' || ''} ${address?.address_neighborhood || '--'} - CEP: ${address?.address_zip_code || '--'} - ${address?.address_city?.name || '--'}/${address?.address_state?.initials || '--'}`}</Text>
              </Text>
            )}

            <Text style={styles.candidateValue}>CONTATOS: </Text>
            <Text style={{ marginLeft: 20, marginTop: 5 }}>
              {data?.candidate.phone && `- Telefone: ${data?.candidate.phone || '--'}`}
            </Text>
            <Text style={{ marginLeft: 20, marginTop: 5 }}>
              {data?.candidate.cellphone && `- Celular: ${data?.candidate.cellphone || '--'}`}
            </Text>
            <Text style={{ marginLeft: 20, marginTop: 5 }}>- E-MAIL: {data?.candidate?.user?.email || '--'}</Text>

            {/* <Text style={styles.candidateValue}>
              ISENTO: <Text>{data?.is_exemption ? 'Sim' : 'Não'}</Text>
            </Text> */}
            <Text style={styles.candidateValue}>
              SITUAÇÃO DA INSCRIÇÃO: <Text>{data?.registration_status?.name || '=='}</Text>
            </Text>
            {data?.type_of_competition.map((modality) => (
              <Text key={modality.id} style={styles.candidateValue}>
                {modality?.name || '--'}: Sim
              </Text>
            ))}

            {listAttachPCD && listAttachPCD?.attachments?.length > 0 && (
              <Text style={styles.candidateValue}>Anexo de documentos PCD:</Text>
            )}

            {listAttachPCD &&
              listAttachPCD?.attachments?.length > 0 &&
              listAttachPCD.attachments.map((attach, index) => (
                <Link src={attach.url.split('?')[0]} style={styles.linkGroup} key={index}>
                  {`- ${attach.name}`}
                </Link>
              ))}

            {/* {data?.attachments?.medical_report &&
              data?.attachments?.medical_report.map((document, ind) => (
                <Link src={document.url.split('?')[0]} style={styles.linkGroup} key={ind}>
                  {`- ${document.name}`}
                </Link>
              ))} */}
            {data?.special_conditions && data?.special_conditions?.length > 0 && (
              <Text style={styles.specialCondition}>Condições especiais:</Text>
            )}
            {data?.special_conditions?.map((item) => (
              <React.Fragment key={item?.id}>
                <Text style={styles.textSpecialCondition}>{item?.name}</Text>
                {item?.attachments?.map((file) => (
                  <Text key={file.id} style={{ fontSize: 8, marginLeft: 30 }}>
                    <Link src={file.url.split('?')[0]} style={styles.linkText} key={Math.random()}>
                      {`- ${file.name}`}
                    </Link>
                  </Text>
                ))}
              </React.Fragment>
            ))}

            {(exemptions ?? []).length > 0 && (
              <View style={{ marginTop: 15 }}>
                <Text style={{ fontWeight: 'bold', marginBottom: 7 }}>DADOS DA ISENÇÃO:</Text>
                {exemptions?.map((exemption) => (
                  <View key={exemption.id} style={{ marginBottom: 7, paddingLeft: 15 }}>
                    <Text>Tipo: {exemption.type}</Text>
                    <Text>Situação: {exemption.status}</Text>
                    {exemption.subtype && (
                      <Text>
                        Subtipo: {typeof exemption.subtype === 'string' ? exemption.subtype : exemption.subtype.name}
                      </Text>
                    )}
                    {exemption.detail && <Text>Detalhes: {exemption.detail}</Text>}
                    {exemption.attachments?.map((attachment) => (
                      <Link key={attachment.id} src={attachment.file} style={styles.linkText}>
                        {attachment.name}
                        {attachment.description ? ` — ${attachment.description}` : ''}
                      </Link>
                    ))}
                  </View>
                ))}
              </View>
            )}

            {uploadedDocuments.length > 0 && (
              <View style={{ marginTop: 15 }}>
                <Text style={{ fontWeight: 'bold', marginBottom: 7 }}>DOCUMENTOS ENVIADOS:</Text>
                {uploadedDocuments.map((document) => {
                  if (!document.attachment) return null;

                  return (
                    <View key={document.type} style={{ marginBottom: 6, paddingLeft: 15 }}>
                      <Text>{document.label}</Text>
                      <Link src={getAttachmentUrl(document.attachment)} style={styles.linkText}>
                        {document.attachment.name}
                      </Link>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default memo(ProofRegistration);
